// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

export const REMOTE_LOGIN_REQUIRED_CODE = 'REMOTE_LOGIN_REQUIRED';
export const REMOTE_LOGIN_USER_MISMATCH_CODE = 'REMOTE_LOGIN_USER_MISMATCH';

const REMOTE_ACCESS_TOKEN_KEY_PREFIX = 'eigent-remote-control:access-token:';
const REMOTE_LOGIN_RETURN_KEY_PREFIX = 'eigent-remote-control:login-return:';

function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function remoteAccessTokenKey(sessionId: string): string {
  return `${REMOTE_ACCESS_TOKEN_KEY_PREFIX}${sessionId}`;
}

function remoteLoginReturnKey(state: string): string {
  return `${REMOTE_LOGIN_RETURN_KEY_PREFIX}${state}`;
}

function randomState(): string {
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/$/, '');
}

function localSiteOrigin(): string {
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//localhost:3000`;
}

export function remoteControlSiteOrigin(): string {
  const configured = import.meta.env.VITE_EIGENT_SITE_ORIGIN;
  if (typeof configured === 'string' && /^https?:\/\//.test(configured)) {
    return stripTrailingSlash(configured);
  }

  if (window.location.hostname === 'test-remote.eigent.ai') {
    return 'https://test.eigent.ai';
  }
  if (window.location.hostname === 'remote.eigent.ai') {
    return 'https://www.eigent.ai';
  }
  return localSiteOrigin();
}

export function getRemoteAccessToken(sessionId: string): string {
  return sessionStorageOrNull()?.getItem(remoteAccessTokenKey(sessionId)) || '';
}

export function setRemoteAccessToken(sessionId: string, token: string): void {
  sessionStorageOrNull()?.setItem(remoteAccessTokenKey(sessionId), token);
}

export function clearRemoteAccessToken(sessionId: string): void {
  sessionStorageOrNull()?.removeItem(remoteAccessTokenKey(sessionId));
}

export function getRemoteLoginReturnUrl(state: string): string {
  return sessionStorageOrNull()?.getItem(remoteLoginReturnKey(state)) || '';
}

export function clearRemoteLoginReturnUrl(state: string): void {
  sessionStorageOrNull()?.removeItem(remoteLoginReturnKey(state));
}

export function buildRemoteLoginReturnUrl(linkToken: string): string {
  const token = linkToken.trim();
  if (!token) {
    throw new Error('Remote control link token is unavailable.');
  }

  const returnUrl = new URL(window.location.href);
  const hashParams = new URLSearchParams(returnUrl.hash.replace(/^#/, ''));
  hashParams.set('t', token);
  returnUrl.hash = hashParams.toString();
  return returnUrl.toString();
}

export function startRemoteLoginRedirect(linkToken: string): void {
  const storage = sessionStorageOrNull();
  if (!storage) {
    throw new Error('Browser session storage is unavailable.');
  }

  const state = randomState();
  storage.setItem(
    remoteLoginReturnKey(state),
    buildRemoteLoginReturnUrl(linkToken)
  );

  const callbackUrl = new URL('/remote-auth/callback', window.location.origin);
  callbackUrl.searchParams.set('state', state);

  const redirectUrl = new URL('/signin', remoteControlSiteOrigin());
  redirectUrl.searchParams.set('callbackUrl', callbackUrl.toString());
  window.location.assign(redirectUrl.toString());
}
